import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEO from "../components/SEO";
import { ArrowRight, CheckCircle, ArrowDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMAGE = "/images/saloni-author.png";

export default function HomePage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${API}/blogs?limit=3`);
        setBlogs(response.data);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  const expertise = [
    {
      title: "Compliance Strategy",
      description: "510(k), CE Marking, PCCP submissions with 100% success rate",
      color: "bg-primary/10 text-primary",
    },
    {
      title: "AI Governance",
      description: "EU AI Act, FDA Guidance, and compliance frameworks",
      color: "bg-blue-100 text-blue-700",
    },
    {
      title: "Quality Management",
      description: "ISO 13485, ISO 14971, and risk management systems",
      color: "bg-amber-100 text-amber-700",
    },
    {
      title: "Risk Management Leadership",
      description: "FDA inspections, audits, and global regulatory alignment",
      color: "bg-emerald-100 text-emerald-700",
    },
  ];

  const valueProps = [
    {
      title: "Deep Compliance Expertise",
      description: "Play a critical role in successful FDA 510(k), CE marking, and PCCP initiatives by partnering with regulatory, quality, and engineering teams to drive compliant, efficient submissions across global markets.",
    },
    {
      title: "AI First Approach",
      description: "Leverage AI/ML tools to reduce compliance overhead, accelerate delivery, and improve regulatory readiness while staying aligned with evolving global regulations.",
    },
    {
      title: "Executive Communication",
      description: "Provide clear risk explanation to leadership teams, translating complex regulatory requirements into actionable business strategies that drive compliance and innovation.",
    },
  ];

  return (
    <>
      <SEO
        title="Saloni Shah | AI Governance & MedTech Compliance Expert"
        description="Expert in AI governance, SaMD regulations, and MedTech compliance. 8+ years helping companies safely ship AI-enabled medical devices in regulated markets."
        url="/"
        image={HERO_IMAGE}
      />

      {/* Hero Section - Full screen with photo background */}
      <section
        className="min-h-screen flex items-center justify-center relative"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
        data-testid="hero-section"
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />

        {/* Content - Right aligned */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full flex justify-end">
          <div className="max-w-xl text-right">
            <h1 className="font-serif font-bold text-2xl md:text-3xl lg:text-4xl text-white leading-tight mb-6 animate-fade-in-up drop-shadow-lg">
              I help companies safely ship SaMD & AI-enabled digital products in regulated markets.
            </h1>

            <p className="text-base md:text-lg text-white/90 mb-10 animate-fade-in-up animation-delay-200 drop-shadow-md">
              8+ years of global compliance experience with a proven track record in AI-enabled regulated products.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-end animate-fade-in-up animation-delay-300">
              <Link
                to="/about"
                className="bg-neutral-900 text-white hover:bg-neutral-800 px-8 py-3 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 text-center"
                data-testid="hero-cta-about"
              >
                Learn About My Approach
              </Link>
              <Link
                to="/blog"
                className="bg-neutral-900 text-white hover:bg-neutral-800 px-8 py-3 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 text-center"
                data-testid="hero-cta-blog"
              >
                Read My Insights
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ArrowDown className="w-6 h-6 text-white/70" />
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-neutral-50" data-testid="expertise-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif font-semibold text-3xl md:text-4xl text-neutral-900 mb-4">
              Trusted by MedTech Leaders
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              8+ years of global compliance experience with a proven track record in AI enabled regulated products
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {expertise.map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl border border-neutral-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid={`expertise-card-${index}`}
              >
                <div className={`w-12 h-12 ${item.color} rounded-lg flex items-center justify-center mb-4`}>
                  <CheckCircle className="w-6 h-6" />
                </div>
                <h3 className="font-serif font-semibold text-xl text-neutral-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-neutral-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Key Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <p className="font-serif font-semibold text-4xl text-primary mb-2">8+</p>
              <p className="text-neutral-600">Years Experience in Regulated Industry</p>
            </div>
            <div>
              <p className="font-serif font-semibold text-4xl text-primary mb-2">12+</p>
              <p className="text-neutral-600">Submissions (FDA 510(k), CE Marking and more)</p>
            </div>
            <div>
              <p className="font-serif font-semibold text-4xl text-primary mb-2">100%</p>
              <p className="text-neutral-600">Audit Success Rate & Zero Critical Findings</p>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-20 bg-white" data-testid="value-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif font-semibold text-3xl md:text-4xl text-neutral-900 mb-4">
              What Sets Me Apart
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Combining deep regulatory expertise with AI innovation to help companies navigate complex compliance landscapes
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {valueProps.map((item, index) => (
              <div key={index} className="text-center">
                <div className={`w-16 h-16 ${
                  index === 0 ? "bg-primary/10" : index === 1 ? "bg-blue-100" : "bg-amber-100"
                } rounded-full flex items-center justify-center mx-auto mb-6`}>
                  <CheckCircle className={`w-8 h-8 ${
                    index === 0 ? "text-primary" : index === 1 ? "text-blue-700" : "text-amber-700"
                  }`} />
                </div>
                <h3 className="font-serif font-semibold text-xl text-neutral-900 mb-4">
                  {item.title}
                </h3>
                <p className="text-neutral-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Insights */}
      {blogs.length > 0 && (
        <section className="py-20 bg-neutral-50" data-testid="insights-section">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex justify-between items-center mb-12">
              <div>
                <h2 className="font-serif font-semibold text-3xl md:text-4xl text-neutral-900 mb-2">
                  Recent Insights
                </h2>
                <p className="text-neutral-600">
                  Thoughts on AI governance and regulatory strategy.
                </p>
              </div>
              <Link
                to="/blog"
                className="hidden md:flex items-center gap-2 text-primary hover:text-primary-light font-medium transition-colors"
                data-testid="view-all-insights"
              >
                View All
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogs.map((blog) => (
                <Link
                  key={blog.id}
                  to={`/blog/${blog.slug}`}
                  className="bg-white rounded-xl overflow-hidden border border-neutral-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group"
                  data-testid={`blog-card-${blog.slug}`}
                >
                  {blog.featured_image && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={blog.featured_image}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <span className="text-xs font-medium text-primary uppercase tracking-wider">
                      {blog.category}
                    </span>
                    <h3 className="font-serif font-semibold text-lg text-neutral-900 mt-2 mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {blog.title}
                    </h3>
                    <p className="text-neutral-600 text-sm line-clamp-2">
                      {blog.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-8 text-center md:hidden">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-primary font-medium"
              >
                View All Insights
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-primary/10" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif font-semibold text-3xl md:text-4xl text-neutral-900 mb-6">
            Ready to Navigate Regulatory Complexity?
          </h2>
          <p className="text-lg text-neutral-600 mb-8 max-w-2xl mx-auto">
            Let's discuss how I can help your team ship compliant AI products while maintaining innovation velocity.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/about#contact"
              className="bg-primary text-white hover:bg-primary-light px-8 py-3 rounded-lg font-medium transition-all duration-300"
              data-testid="cta-contact"
            >
              Get In Touch
            </Link>
            <Link
              to="/blog"
              className="border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-3 rounded-lg font-medium transition-all duration-300"
            >
              Read My Insights
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
